import { specialChars, tryDecodeFilePath } from '@/utils/constants';
import { safelyParseDate } from '@/utils/date';
import { emailSchema } from '@/utils/validation';
import * as Yup from 'yup';

export const VIEW_TYPES = {
	create: 'create',
	update: 'update',
};

export const getFieldDefault = (field, user) => {
	if (!field) return null;
	const fieldConfig = field.field ?? {};

	if (fieldConfig?.default) {
		if (typeof fieldConfig.default === 'string') {
			const [type, value] = fieldConfig.default.split('.');
			if (user && value) {
				if (type === 'user') {
					return user[value];
				} else if (type === 'brokerGroup') {
					if (value === 'countryCode') {
						return fieldConfig.options.find(
							(o) => o.countryCode === user.broker.countryCode,
						)?.value;
					}
					if (value === 'id') {
						return user?.brokerGroupId ?? user?.broker?.id ?? null;
					}
					return user.broker[value];
				}
			}
		}
		return fieldConfig.default;
	}
	if (fieldConfig.endpoint) {
		return null;
	}
	switch (fieldConfig.type) {
		case 'string':
			return '';
		case 'number':
			return 0;
		case 'date-string':
			return null;
		case 'boolean':
			return false;
		default:
			return null;
	}
};

export const isProspectFieldVisible = (field, view) => {
	const prospectConfig = field.visible?.prospect;
	if (typeof prospectConfig === 'object' && prospectConfig !== null) {
		return Boolean(prospectConfig[view]);
	}
	return Boolean(prospectConfig); //fallback
};

export const mapFieldsToDefaults = ({
	fields: fieldsArray = [],
	view,
	user,
	existingClient,
	isProspect,
}) => {
	const fields = fieldsArray.reduce((acc, field) => {
		const group = field.field.group;
		if (!acc[group]) acc[group] = [];
		const isVisible = isProspect ? isProspectFieldVisible(field, view) : field.visible?.[view];
		if (isVisible) {
			acc[group].push(field);
		}
		return acc;
	}, {});

	if (Object.keys(fields).length === 0) return {};
	const { contact, ...groups } = fields;

	const defaultValues = Object.values(groups)
		.flat()
		.reduce((acc, field) => {
			let existingValue = existingClient?.[field.source]?.[field.name];
			if (typeof existingValue === 'string' && typeof field.field?.default === 'boolean') {
				existingValue = ['no', 'false'].includes(existingValue.toLowerCase())
					? false
					: ['yes', 'true'].includes(existingValue.toLowerCase())
						? true
						: Boolean(existingValue);
			} else if (existingValue && field.field?.type === 'date-string') {
				existingValue = safelyParseDate(existingValue);
			}
			const newValue = existingValue ?? getFieldDefault(field, user);
			acc[field.name] = newValue;

			if (typeof field.field?.default === 'string' && newValue) {
				// console.log(`${field.name} in the block`, newValue);
				const [type, value] = field.field.default.split('.');
				if (value) {
					const clientDocId = existingClient?.[field.source]?.[`${field.name}-docId`];
					if (type === 'user') {
						acc[`${field.name}-docId`] = clientDocId ?? user?.userId;
						acc[`${field.name}-obj`] = clientDocId ? user : null;
					} else if (type === 'brokerGroup') {
						acc[`${field.name}-docId`] =
							clientDocId ??
							existingClient?.[field.source]?.brokerGroupId ??
							user?.brokerGroupId;
						acc[`${field.name}-obj`] = clientDocId ? user?.broker : null;
					}
					acc[`${field.name}-type`] = type;
				}
			}
			if (field.name === 'StandardIndustryCode' && typeof newValue === 'string') {
				const match = newValue.match(/\[(\d+)\]/);
				if (match && match[1]) {
					acc[field.name] = match[1];
				}
			}
			return acc;
		}, {});

	return {
		...defaultValues,
		...(existingClient && {
			insightId: existingClient.firebase?.insightId,
			docId: existingClient.firebase?.docId,
		}),
		contacts: [
			{
				isMainContact: true,
				...contact?.reduce(
					(acc, field) => ({
						...acc,
						[field.name]:
							existingClient?.[field.source]?.[field.name] ??
							getFieldDefault(field, user),
					}),
					{},
				),
			},
		],
	};
};

const isVisible = (field, view) => field.visible[view] !== false;

const applyRequired = (schema, field) => {
	if (field.required) {
		return schema.required(`${field.label ?? field.name} is required`);
	}
	return schema.notRequired();
};

const applyMinMax = (schema, field) => {
	if (typeof field.min === 'number') {
		schema = schema.min(field.min);
	}
	if (typeof field.max === 'number') {
		schema = schema.max(field.max);
	}
	return schema;
};

const applyStringValidation = (schema, field) => {
	switch (field.validation) {
		case 'email':
			return emailSchema(false, schema, `${field.label ?? field.name}`);
		default:
			return schema;
	}
};

export const getFieldValidation = (field, view) => {
	const fieldConfig = field?.field ?? {};
	const mergedField = {
		...fieldConfig,
		...field,
	};

	if (!isVisible(mergedField, view)) return null;

	// console.log(`Merged field ${mergedField.name}`, mergedField);
	if (mergedField.endpoint) {
		let schema = Yup.mixed();
		schema = applyRequired(schema, mergedField);
		return schema;
	}

	switch (mergedField.type) {
		case 'string': {
			let schema = Yup.string();

			schema = applyRequired(schema, mergedField);
			schema = applyMinMax(schema, mergedField);
			schema = applyStringValidation(schema, mergedField);

			return schema;
		}

		case 'number': {
			let schema = Yup.number().typeError(`${field.label ?? field.name} must be a number`);

			// Selects, radios, lookups → integer IDs
			if (field.fieldType === 'select' || field.fieldType === 'radio') {
				schema = schema.integer();
			}

			// If field is StateId or AuthorisedRep*Id or UsualDebtorId,
			// check that value is greater than 0
			if (
				field.required &&
				(['StateId', 'UsualDebtorId'].includes(field.name) ||
					field.name.startsWith('AuthorisedRep'))
			) {
				schema = schema.min(1, `${field.name} is required`);
			}

			schema = applyRequired(schema, field);
			return schema;
		}

		case 'boolean': {
			let schema = Yup.boolean();
			schema = applyRequired(schema, field);
			return schema;
		}

		case 'date-string': {
			let schema = Yup.date().typeError(`${field.label ?? field.name} must be a valid date`);

			schema = applyRequired(schema, field);
			return schema;
		}

		default:
			return null;
	}
};

const buildShapeFromFields = (fields, view, context = {}) => {
	const shape = fields.reduce((acc, field) => {
		const validation = getFieldValidation(field, view);
		if (!validation) return acc;
		if (field.name !== 'Name') {
			acc[field.name] = validation;
			return acc;
		}
		acc[field.name] = validation
			.test(
				'no-special-characters',
				`${field.label} contains invalid characters.`,
				(value) => {
					if (!value) return true;
					return !specialChars.test(value.trim());
				},
			)
			.test('no-end-in-fullstop', `${field.label} cannot end with full stop`, (value) => {
				if (!value) return true;
				return !value.endsWith('.');
			})
			.test(
				'client-name-exists',
				'Another client already exists with this name in your brokerage',
				async function (value) {
					if (!value) return true;
					// userId: Firestore doc id of the user whose brokerage to search.
					const { checkClientExists, userId } = context;

					if (!checkClientExists || !userId) return true;


					try {
						const result = await checkClientExists({
							name: value,
							userId: this.parent?.ownerDocId ?? userId,
							insightId: this.parent.insightId,
						});

						return result;
					} catch (err) {
						console.error('Error getting client exists', err);
						return true;
					}
				},
			)
			.test(
				'client-folder-exists',
				'There is an existing folder with this name',
				async function (value) {
					// console.log(`test: unique-folder for name "${value}"`);
					if (!value) return true;
					const { folders } = context;

					if ((folders ?? []).length === 0) return true;

					const folderExists = folders.some((folder) => tryDecodeFilePath(folder, value));
					// console.log(`test: unique-folder for name "${value}"`, folders, folderExists);
					return !folderExists;
				},
			);
		return acc;
	}, {});

	return shape;
};

const buildContactShape = (fields, view, context = {}) => {
	const shape = fields.reduce((acc, field) => {
		const validation = getFieldValidation(field, view);
		if (!validation) return acc;
		acc[field.name] = validation;
		return acc;
	}, {});

	return shape;
};

export const buildValidationSchema = ({ fields, view, context }) => {
	const rootFields = fields.filter((f) => f.field.group !== 'contact');
	const contactFields = fields.filter((f) => f.field.group === 'contact');

	const rootShape = buildShapeFromFields(rootFields, view, context);
	const contactShape = buildContactShape(contactFields, view, context);

	const contactSchema = Yup.array()
		.of(
			Yup.object().shape({
				isMainContact: Yup.boolean(),
				...contactShape,
			}),
		)
		.min(1, 'At least one contact is required')
		.test(
			'has-main-contact',
			'At least one contact must be the main contact',
			(contacts = []) => {
				// Single-contact edit/add drawers shouldn't require a primary contact...
				if (['addContact', 'editContact'].includes(context?.variant)) {
					return true;
				}
				return contacts.some((c) => c?.isMainContact);
			},
		)
		.test(
			'only-one-main-contact',
			'Only one contact can be marked as the main contact',
			(contacts = []) => contacts.filter((c) => c?.isMainContact).length <= 1,
		);

	const schema = ['addContact', 'editContact'].includes(context?.variant)
		? Yup.object().shape({
				contacts: contactSchema,
			})
		: Yup.object().shape({
				...rootShape,
				contacts: contactSchema,
			});

	// console.log(`build schema ${context?.variant}`, { schema, contactSchema, context });

	return schema;
};
